import { setVolume, getVolume, setMuted, isMuted, resumeAudio } from "@/audio/SoundManager";
import { VoiceVolume } from "@/audio/VoiceVolume";

/**
 * Sidebar audio controls — two independent sliders + mute toggles:
 *
 *   - "Sound FX": procedural UI / footstep / chat-chime SFX
 *     (driven by SoundManager).
 *   - "Voice & Music": LiveKit-forwarded voice + remote music + the
 *     host's own jukebox playback (driven by VoiceVolume; subscribed by
 *     MusicPlayer.playbackGain and SpatialAudioController.masterGain).
 *
 * The split exists so muting UI chimes doesn't silence other players,
 * and vice versa.
 */
export class AudioControls {
  private fxMuteBtn = document.getElementById("mute-btn") as HTMLButtonElement;
  private fxSlider = document.getElementById("volume-slider") as HTMLInputElement;
  private voiceMuteBtn = document.getElementById("voice-mute-btn") as HTMLButtonElement;
  private voiceSlider = document.getElementById("voice-volume-slider") as HTMLInputElement;

  init(): void {
    // ── Sound FX ───────────────────────────────────────────────────
    this.fxSlider.value = String(getVolume());
    this.updateFxMuteIcon();

    this.fxMuteBtn.addEventListener("click", () => {
      resumeAudio();
      setMuted(!isMuted());
      this.updateFxMuteIcon();
    });

    this.fxSlider.addEventListener("input", () => {
      resumeAudio();
      const v = parseFloat(this.fxSlider.value);
      setVolume(v);
      if (v > 0 && isMuted()) {
        setMuted(false);
        this.updateFxMuteIcon();
      }
    });

    // ── Voice & Music ──────────────────────────────────────────────
    this.voiceSlider.value = String(VoiceVolume.getVolume());
    this.updateVoiceMuteIcon();

    this.voiceMuteBtn.addEventListener("click", () => {
      VoiceVolume.setMuted(!VoiceVolume.isMuted());
      this.updateVoiceMuteIcon();
    });

    this.voiceSlider.addEventListener("input", () => {
      const v = parseFloat(this.voiceSlider.value);
      VoiceVolume.setVolume(v);
      if (v > 0 && VoiceVolume.isMuted()) {
        VoiceVolume.setMuted(false);
        this.updateVoiceMuteIcon();
      }
    });
  }

  private updateFxMuteIcon(): void {
    const muted = isMuted();
    this.fxMuteBtn.textContent = muted ? "🔇" : "🔊";
    this.fxMuteBtn.title = muted ? "Unmute Sound FX" : "Mute Sound FX";
  }

  private updateVoiceMuteIcon(): void {
    const muted = VoiceVolume.isMuted();
    this.voiceMuteBtn.textContent = muted ? "🔇" : "🔊";
    this.voiceMuteBtn.title = muted ? "Unmute Voice & Music" : "Mute Voice & Music";
  }
}
